import { Alert, Form, Input, Modal } from 'antd';
import { useSubmission } from '../../../shared/hooks/use-submission';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import type { AlertView } from '../alerts.api';

interface ResolveAlertModalProps {
  /** The alert being resolved; null keeps the modal closed. */
  alert: AlertView | null;
  /** Does the request and shows its own toast; a throw is shown inline, here, and keeps the modal open. */
  onResolve: (alert: AlertView, note: string) => Promise<void>;
  onClose: () => void;
}

/** Resolving closes an alert for good, so the person says what was done (gitaumoses4/chukua#95). The server refuses a blank note too. */
export function ResolveAlertModal({ alert, onResolve, onClose }: ResolveAlertModalProps) {
  const [form] = Form.useForm<{ note: string }>();
  const note = Form.useWatch('note', form);
  const { error, submitting, submit, clearError } = useSubmission();

  async function confirm(values: { note: string }) {
    if (!alert) return;
    const done = await submit(
      () => onResolve(alert, values.note.trim()).then(() => true),
      (err) => apiErrorMessage(err) ?? 'That did not go through. Try again.',
    );
    if (!done) return;
    form.resetFields();
    onClose();
  }

  return (
    <Modal
      open={alert !== null}
      title={alert ? `Resolve ${alert.ruleName} on ${alert.truckRegistration}` : 'Resolve alert'}
      okText="Resolve"
      okButtonProps={{ loading: submitting, disabled: !note?.trim() }}
      onOk={() => form.submit()}
      onCancel={onClose}
      afterOpenChange={clearError}
      destroyOnHidden
    >
      {error && <Alert className="mb-4" type="error" title={error} showIcon />}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={confirm} disabled={submitting}>
        <Form.Item name="note" label="What was done" rules={[{ required: true, whitespace: true, message: 'Say what was done' }]}>
          <Input.TextArea rows={3} maxLength={500} showCount autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}
